import { AxiosError } from "axios";
import { ApiProvider } from "../ApiProvider";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleError } from "../getThunkAndSlice";

type TInitialState = {
	isLoading: boolean;
	isInitialized: boolean;
	error: string | null;
};

export type TPayload = {
	cc_emails: Array<string>;
	description: string;
	email: string;
	subject: string;
};

export type TFulfillmentHelpRequestPayload = {
	invoiceId: number;
	commonHelp: string;
	additionalInfo?: string;
	name: string;
	companyName: string;
	subject: string;
	description: string;
	attachment: File | null;
};

type TFulfillmentHelpRequestResponse = {
	supportResponse?: { id?: number };
	success?: boolean;
};

const initialState: TInitialState = {
	isLoading: false,
	isInitialized: false,
	error: null,
};

export const createHelpAndSupportTicket = createAsyncThunk<unknown, TPayload>(
	"helpAndSupport/createHelpAndSupportTicket",
	async (payload, { rejectWithValue }) => {
		try {
			return await ApiProvider.default.post("/api/System/CreateFreshDeskSupportTicket", payload);
		} catch (error) {
			if (error instanceof AxiosError) return rejectWithValue(error.message);
			else return rejectWithValue("Unknown error");
		}
	},
);

export const createFulfillmentHelpRequest = createAsyncThunk<unknown, TFulfillmentHelpRequestPayload>(
	"helpAndSupport/createFulfillmentHelpRequest",
	async (payload, { rejectWithValue }) => {
		try {
			const response = await ApiProvider.default.postWithFile<TFulfillmentHelpRequestResponse | string>(
				"/api/Fulfillment/CreateFulfillmentHelpRequestEmail",
				{
					CommonHelp: payload.commonHelp,
					Desc: payload.additionalInfo ?? "",
					name: payload.name,
					company_name: payload.companyName,
					subject: payload.subject,
					description: encodeURI(payload.description),
					...(payload.attachment ? { attachment: payload.attachment } : {}),
				},
			);

			const result: TFulfillmentHelpRequestResponse = typeof response === "string" ? JSON.parse(response) : response;
			const freshdeskId = result?.supportResponse?.id;

			if (freshdeskId) {
				await ApiProvider.default.post("/api/Fulfillment/AttachFreshdeskIdToInvoice", {
					InvoiceId: payload.invoiceId,
					FreshdeskId: freshdeskId,
				});
			}

			return result;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	},
);

export const helpAndSupportSlice = createSlice({
	name: "helpAndSupport",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(createHelpAndSupportTicket.pending, (state) => {
				state.isLoading = true;
				state.isInitialized = true;
			})
			.addCase(createHelpAndSupportTicket.fulfilled, (state) => {
				state.isLoading = false;
			})
			.addCase(createHelpAndSupportTicket.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload as string;
			})
			.addCase(createFulfillmentHelpRequest.pending, (state) => {
				state.isLoading = true;
				state.isInitialized = true;
			})
			.addCase(createFulfillmentHelpRequest.fulfilled, (state) => {
				state.isLoading = false;
			})
			.addCase(createFulfillmentHelpRequest.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload as string;
			});
	},
});
